import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Collapse,
  Fade,
  Link,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
} from '@mui/material'
import GoogleIcon from '@mui/icons-material/Google'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseIcon from '@mui/icons-material/Close'
import logo from '../images/logo.png'
import svgLogo from '/assets/images/SVGLogo.svg'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '../hooks'
import { setAuthenticated, setUserData, setPlayerName } from '../stores/UserStore'
import store from '../stores'
import { AuthError } from '../services/authService'
import { UserRole } from '../types/user'
import { coerceUserProfile, readParseGoogleLinked, userProfileToFullUserData } from '../account'
import authService from '../services/authService'
import { subscribeSessionExpiredNotice } from '../services/parseCloud'
import SecurityService, { SecuritySettings } from '../services/securityService'
import { ServiceFactory } from '../core/factories/ServiceFactory'
import { getServerBaseUrl } from '@/utils/serverUrl'
import { signupWaitlistUrl } from '../utils/signupWaitlist'
import {
  AuthMessages,
  OAUTH_SIGNUP_CLOSED_STORAGE_KEY,
  confirmPasswordMessage,
  signInErrorView,
  signUpErrorView,
  signInPasswordMessage,
  signUpPasswordMessage,
} from './AuthenticationDialog.messages'
import {
  writeGoogleOAuthHandoffToLocalStorage,
  writeGoogleOAuthSignupConsent,
} from '../utils/googleOAuthClient'
import { buildSignupConsent } from '../consent/consentService'
import { TERMS_NOTICE_ACTION } from '../consent/consentCopy'
import { useLegalLinks } from '../consent/useLegalLinks'
import MarketingEmailCheckbox from './consent/MarketingEmailCheckbox'
import TermsNotice from './consent/TermsNotice'
import { useRoutingStore } from '../stores/routingStore'
import { markWorkspaceLandingHandled } from './MainMenu/utils/mainMenuLanding'
import { PasswordInput, TextInput } from './Inputs'
import { AUTH_BRAND_COPY } from './auth/authBrandCopy'
import {
  forgotPasswordDialogActionsSx,
  forgotPasswordDialogPaperSx,
  forgotPasswordDialogTitleSx,
} from './forgotPasswordDialogSx'

// Import SCSS styles
import './AuthenticationDialog.scss'
import { getDisplayName } from '../utils/personName'
import { peekPendingCompanyInvite } from '../services/CompanyInviteLinkService'

export type ToastSeverity = 'error' | 'success'
export interface AuthToastState {
  message: string
  severity: ToastSeverity
}

// Interface definitions
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

// TabPanel component
function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
      style={{ display: value === index ? 'block' : 'none' }}
    >
      <AnimatePresence mode="wait">
        {value === index && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Holds a field's blur while a pointer press that started inside the form is in progress.
 *
 * The browser blurs the password field on pointerdown, and hiding the password hint then
 * collapses it, which moves the checkbox and the submit button up. If they move before the
 * release, the press and release land on different elements and the click is lost (it goes
 * to the form). So a blur during such a press only hides after the press is over, and only
 * once the click that belongs to it has been dispatched (the 0ms timeout). Any other blur
 * (Tab, a press outside the form) hides at once.
 */
function useBlurHeldDuringFormPress(hide: () => void) {
  const hideRef = useRef(hide)
  hideRef.current = hide
  const pressingRef = useRef(false)
  const hidePendingRef = useRef(false)
  const endPressListenersRef = useRef<(() => void) | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearPress = useCallback(() => {
    endPressListenersRef.current?.()
    endPressListenersRef.current = null
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
    pressingRef.current = false
  }, [])

  const cancel = useCallback(() => {
    clearPress()
    hidePendingRef.current = false
  }, [clearPress])

  useEffect(() => cancel, [cancel])

  const onFormPointerDown = useCallback(() => {
    // A hide still pending from an earlier press stays pending and waits for this one.
    clearPress()
    pressingRef.current = true
    const endPress = () => {
      endPressListenersRef.current?.()
      endPressListenersRef.current = null
      pressingRef.current = false
      if (!hidePendingRef.current) return
      hideTimerRef.current = setTimeout(() => {
        hideTimerRef.current = null
        // Focusing the field again clears the pending hide, so a press back into it wins.
        if (!hidePendingRef.current) return
        hidePendingRef.current = false
        hideRef.current()
      }, 0)
    }
    // On window, so a release outside the form still ends the press.
    window.addEventListener('pointerup', endPress, true)
    window.addEventListener('pointercancel', endPress, true)
    endPressListenersRef.current = () => {
      window.removeEventListener('pointerup', endPress, true)
      window.removeEventListener('pointercancel', endPress, true)
    }
  }, [clearPress])

  const onFieldFocus = useCallback(() => {
    hidePendingRef.current = false
  }, [])

  const onFieldBlur = useCallback(() => {
    if (pressingRef.current) {
      hidePendingRef.current = true
      return
    }
    hidePendingRef.current = false
    hideRef.current()
  }, [])

  return { onFormPointerDown, onFieldFocus, onFieldBlur, cancel }
}

// Main component
export default function AuthenticationDialog() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // SC-438: someone arriving from a workspace invite link is almost always new (one company per
  // account), so Create Account opens first; Sign In stays a click away.
  const [pendingInvite] = useState(() => peekPendingCompanyInvite())
  const [tabValue, setTabValue] = useState(() => (pendingInvite ? 1 : 0))
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Create Account only. Names are asked in onboarding's "Your Sevv self" step (SC-438).
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState('')
  const [allowedDomainsText, setAllowedDomainsText] = useState<string>('')
  const [passwordError, setPasswordError] = useState(false)
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [error, setError] = useState<AuthError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  // Create Account only: there the hint sits above the checkbox and the submit button.
  const signUpPasswordBlur = useBlurHeldDuringFormPress(() => setPasswordFocused(false))
  const [googleLoading, setGoogleLoading] = useState(false)
  const [highlightGoogleButton, setHighlightGoogleButton] = useState(false)
  // Unticked by default: marketing email needs an active opt-in (SC-360).
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false)
  const dispatch = useAppDispatch()
  const [toast, setToast] = useState<AuthToastState | null>(null)
  const settings = useAppSelector((state) => state.workspace.settings)
  const [logoLoaded, setLogoLoaded] = useState(true)
  const isRegistrationClosed = useMemo(() => (settings as any)?.registration_enabled === false, [settings])
  const waitlistUrl = signupWaitlistUrl(settings)

  // Centralized message helpers (plan: toast vs inline, no duplicate same message)
  const showToast = (message: string, severity: ToastSeverity) => {
    setToast({ message, severity })
  }
  const clearToast = () => setToast(null)

  const showInlineError = (
    field: 'email' | 'password',
    message: string,
    options?: { alsoSetError?: boolean; errorCode?: number }
  ) => {
    if (field === 'email') {
      setEmailError(true)
      setEmailErrorMessage(message)
    } else {
      setPasswordError(true)
    }
    if (options?.alsoSetError) {
      setError({
        code: options.errorCode ?? 999,
        message,
        field,
      })
    }
  }

  const clearAllErrors = () => {
    setError(null)
    setEmailError(false)
    setEmailErrorMessage('')
    setPasswordError(false)
    setConfirmPasswordError('')
  }

  // Security settings state
  const [securitySettings, setSecuritySettings] = useState<SecuritySettings | null>(null)
  const [passwordValidation, setPasswordValidation] = useState<{ isValid: boolean; errors: string[] }>({ isValid: true, errors: [] })

  // Derived once so the message, the red border and the aria wiring cannot disagree — they
  // each used to re-evaluate their own version of the condition. [SC-208]
  const signInPasswordError = signInPasswordMessage({ passwordEmpty: passwordError, error })
  const signUpPasswordError = signUpPasswordMessage({
    passwordEmpty: passwordError,
    policyErrors: passwordValidation.isValid ? [] : passwordValidation.errors,
    error,
  })
  const showPasswordRequirements =
    passwordFocused || (password.length > 0 && !passwordValidation.isValid)

  // Forgot Password Modal State
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)

  // Google refused a new account because sign-up is closed (SC-443)
  const [showSignupClosedDialog, setShowSignupClosedDialog] = useState(false)

  // Helper function to get the appropriate logo URL
  const getLogoUrl = (logoUrl: string | undefined) => {
    if (!logoUrl) return svgLogo
    // If the URL is a default path like /logo.png, use the imported SVG logo
    if (logoUrl === '/logo.png' || logoUrl === 'logo.png' || logoUrl === '/logo') {
      return svgLogo
    }
    return logoUrl
  }

  // Add platform branding state
  const platformName = settings?.platform_name || 'Sevv'
  const platformUrl = settings?.platform_url || 'https://app.sevv.com'
  const logoUrlState = getLogoUrl(settings?.logo_url) || svgLogo
  const legalLinks = useLegalLinks(platformUrl)
  const primaryColor = theme.palette.primary.main

  // Debug: Log branding settings when they change
  useEffect(() => {
    console.log('[AuthenticationDialog] Branding settings updated:', {
      platformName,
      platformUrl,
      logoUrl: logoUrlState,
      primaryColor,
      settings
    })
  }, [platformName, platformUrl, logoUrlState, primaryColor, settings])

  // Theme provider removed - using SCSS theme instead

  const redirectURL = window.location.origin

  // Session expiration (toast only per plan). SC-452: also the one from a forced
  // sign-out, which fired before this dialog mounted.
  useEffect(() => {
    return subscribeSessionExpiredNotice(() => {
      showToast(AuthMessages.SESSION_EXPIRED, 'error')
    })
  }, [])

  // Auto-dismiss toast (6–7s error, 4–5s success per plan)
  useEffect(() => {
    if (toast) {
      const duration = toast.severity === 'success' ? 5000 : 6500
      const timer = setTimeout(clearToast, duration)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Check for stored OAuth errors when dialog mounts
  // Only show errors if they're recent (within last 5 minutes) and user is not authenticated
  useEffect(() => {
    // Check if user is already authenticated - if so, clear any stale OAuth errors
    const checkAuthAndClearStaleErrors = async () => {
      try {
        const authService = ServiceFactory.getInstance().getAuthService()
        const currentUser = await authService.getCurrentUser()

        // If user is authenticated, clear all OAuth errors (they're stale)
        if (currentUser) {
          console.log('[AuthenticationDialog] User is authenticated, clearing stale OAuth errors')
          sessionStorage.removeItem('oauth_domain_error')
          sessionStorage.removeItem('oauth_error')
          sessionStorage.removeItem(OAUTH_SIGNUP_CLOSED_STORAGE_KEY)
          clearToast()
          setError(null)
          return
        }
      } catch (error) {
        // If we can't check auth (e.g., user not logged in), continue with error checking
        // This is normal when user is not authenticated, don't log as warning
        // Only log actual errors in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[AuthenticationDialog] User not authenticated (normal state)')
        }
      }

      // Google sign-in refused a new account while sign-up is closed (the go-to-market
      // waitlist). A popup rather than a field error: nothing in the form can fix it. [SC-443]
      const oauthSignupClosed = sessionStorage.getItem(OAUTH_SIGNUP_CLOSED_STORAGE_KEY)
      if (oauthSignupClosed) {
        sessionStorage.removeItem(OAUTH_SIGNUP_CLOSED_STORAGE_KEY)
        try {
          const errorTimestamp = JSON.parse(oauthSignupClosed)?.timestamp || 0
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
          if (errorTimestamp > fiveMinutesAgo) {
            setShowSignupClosedDialog(true)
          } else {
            console.log('[AuthenticationDialog] Clearing stale OAuth sign-up closed notice')
          }
        } catch (parseError) {
          console.error('Error parsing OAuth sign-up closed notice:', parseError)
        }
      }

      // Check for domain validation errors from OAuth (only if user is not authenticated)
      const oauthDomainError = sessionStorage.getItem('oauth_domain_error')
      if (oauthDomainError) {
        try {
          const errorData = JSON.parse(oauthDomainError)

          // Only show error if it's recent (within last 5 minutes)
          // OAuth errors should be cleared after successful login, so if they persist, they're stale
          const errorTimestamp = errorData.timestamp || 0
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)

          if (errorTimestamp > fiveMinutesAgo) {
            const msg = errorData.message || AuthMessages.OAUTH_DOMAIN_BLOCKED
            if (errorData.field === 'domain') {
              setEmailError(true)
              setEmailErrorMessage(msg)
              setError({ code: 403, message: msg, field: 'domain' })
            } else {
              showToast(msg, 'error')
              setError({ code: 403, message: msg, field: errorData.field })
            }
          } else {
            // Stale error - clear it
            console.log('[AuthenticationDialog] Clearing stale OAuth domain error')
          }

          // Always clear the stored error after checking
          sessionStorage.removeItem('oauth_domain_error')
        } catch (parseError) {
          console.error('Error parsing OAuth domain error:', parseError)
          sessionStorage.removeItem('oauth_domain_error')
        }
      }

      // Check for general OAuth errors (only if user is not authenticated)
      const oauthError = sessionStorage.getItem('oauth_error')
      if (oauthError) {
        try {
          const errorData = JSON.parse(oauthError)

          // Only show error if it's recent (within last 5 minutes)
          const errorTimestamp = errorData.timestamp || 0
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)

          if (errorTimestamp > fiveMinutesAgo) {
            setError({
              code: 999,
              message: errorData.message,
              field: errorData.field
            })
            showToast(errorData.message || AuthMessages.OAUTH_GENERIC, 'error')
          } else {
            // Stale error - clear it
            console.log('[AuthenticationDialog] Clearing stale OAuth error')
          }

          // Always clear the stored error after checking
          sessionStorage.removeItem('oauth_error')
        } catch (parseError) {
          console.error('Error parsing OAuth error:', parseError)
          sessionStorage.removeItem('oauth_error')
        }
      }
    }

    checkAuthAndClearStaleErrors()
  }, [])

  // Load security settings and allowed domains on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Wrap the entire settings load in a 8 s timeout so a slow cloud
        // call never stalls the login dialog on bad connections.
        const settingsTimeout = new Promise<'timeout'>((resolve) =>
          setTimeout(() => resolve('timeout'), 8000)
        )

        const doLoad = async () => {
          // Password policy is public — load for signup/sign-in validation UI
          try {
            const securityService = SecurityService.getInstance()
            const passwordPolicy = await securityService.getPasswordPolicy()
            setSecuritySettings({ password: passwordPolicy } as SecuritySettings)
          } catch (policyError) {
            // Proceed with null settings; validatePasswordLocally will skip until loaded
          }

          // Load allowed domains (this should work without authentication)
          const workspaceService = ServiceFactory.getInstance().getWorkspaceService()
          const workspaceSettings = await workspaceService.getSettings()
          if (workspaceSettings?.blocked_domains && workspaceSettings.blocked_domains.length > 0) {
            setAllowedDomainsText(workspaceSettings.blocked_domains.join(', '))
          }
        }

        const result = await Promise.race([doLoad(), settingsTimeout])
        if (result === 'timeout') {
          console.warn('[AuthenticationDialog] Settings load timed out — proceeding with defaults')
        }
      } catch (error) {
        console.warn('Could not load settings:', error)
      }
    }
    loadSettings()
  }, [])

  // Reset the form
  const resetForm = () => {
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    clearAllErrors()
    clearToast()
    sessionStorage.removeItem('oauth_domain_error')
    sessionStorage.removeItem('oauth_error')
  }

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    clearAllErrors()
    signUpPasswordBlur.cancel()
    setPasswordFocused(false)
    clearToast()
    sessionStorage.removeItem('oauth_domain_error')
    sessionStorage.removeItem('oauth_error')
  }

  // While sign-up is closed (the go-to-market waitlist) Create Account can't be opened. The
  // settings load after the dialog mounts, so they can arrive as closed with that tab open.
  // [SC-443]
  useEffect(() => {
    if (isRegistrationClosed && tabValue === 1) {
      handleTabChange({} as React.SyntheticEvent, 0)
    }
  }, [isRegistrationClosed, tabValue])

  // Form validation
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password: string) => {
    if (!securitySettings) {
      return password.length >= 8 // Default validation
    }

    const validation = SecurityService.getInstance().validatePasswordLocally(password, securitySettings)
    setPasswordValidation(validation)
    return validation.isValid
  }

  // Enhanced email validation with domain restrictions (inline only per plan)
  const validateEmailWithDomain = async (email: string) => {
    if (!validateEmail(email)) {
      setEmailError(true)
      setEmailErrorMessage(AuthMessages.SIGNUP_EMAIL_INVALID)
      return false
    }

    try {
      const workspaceService = ServiceFactory.getInstance().getWorkspaceService()
      const result = await workspaceService.validateRegistrationEmail(email)
      if (!result.isValid) {
        const msg = result.message || AuthMessages.SIGNUP_DOMAIN_RESTRICTED
        setEmailError(true)
        setEmailErrorMessage(msg)
        return false
      }
    } catch (err) {
      console.warn('Could not validate domain restrictions:', err)
      setEmailError(true)
      setEmailErrorMessage(AuthMessages.SIGNUP_EMAIL_VALIDATION_FAILED)
      return false
    }

    setEmailError(false)
    setEmailErrorMessage('')
    return true
  }

  // Update email change handler
  const handleEmailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value
    setEmail(newEmail)
    if (newEmail && tabValue === 1) {
      await validateEmailWithDomain(newEmail)
    } else {
      setEmailError(false)
      setEmailErrorMessage('')
    }
  }

  const handleToastClose = () => clearToast()

  // Enhanced Sign In handler
  const handleSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    clearAllErrors()
    clearToast()

    const validationErrors: Array<'email' | 'password'> = []
    if (!email || !validateEmail(email)) {
      setEmailError(true)
      setEmailErrorMessage(AuthMessages.SIGNIN_EMAIL_INVALID)
      validationErrors.push('email')
    }
    if (!password) {
      setPasswordError(true)
      validationErrors.push('password')
    }
    if (validationErrors.length > 0) {
      if (validationErrors.length > 1) {
        showToast(AuthMessages.VALIDATION_SUMMARY, 'error')
      }
      return
    }

    setIsLoading(true)

    try {
      console.log('[AuthenticationDialog] Starting sign in process')
      const authResponse = await authService.signIn(email, password)

      const profile = coerceUserProfile(authResponse.profile)
      const userData = userProfileToFullUserData(
        {
          id: authResponse.user.id,
          email: authResponse.user.email,
          username: authResponse.user.username,
          role: (profile.role as UserRole) || 'user',
          createdAt: authResponse.user.createdAt.toISOString(),
        },
        profile,
        readParseGoogleLinked()
      )

      dispatch(setUserData(userData))

      // Update player name mapping for immediate UI updates (only if in a room session)
      const displayName = getDisplayName(userData)
      const sessionId = store.getState().user.sessionId
      if (sessionId) {
        dispatch(setPlayerName({ id: sessionId, name: displayName }))
      }

      // Player name service removed - using centralized nameplate system instead

      dispatch(setAuthenticated(true))
      console.log('[AuthenticationDialog] Sign in successful')

      const searchParams = new URLSearchParams(window.location.search)
      const hasJoinLink = searchParams.get('rid') || searchParams.get('room')
      if (!hasJoinLink) {
        markWorkspaceLandingHandled()
        useRoutingStore.getState().navigateTo('/dashboard', true)
      }

    } catch (err: any) {
      console.error('[AuthenticationDialog] Sign in error:', err)
      const authError = err as AuthError

      // The message and its placement live in signInErrorView so they can be tested without
      // rendering the dialog. [SC-409]
      const view = signInErrorView(authError)
      if (view.inline === 'email') {
        setEmailError(true)
        setEmailErrorMessage(view.message)
      } else if (view.inline === 'password') {
        setPasswordError(true)
      }
      if (view.highlightGoogle) setHighlightGoogleButton(true)
      if (view.toast) showToast(view.message, 'error')
      setError({
        ...authError,
        message: view.message,
        ...(view.errorField ? { field: view.errorField } : {}),
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Enhanced Sign Up handler
  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    clearAllErrors()
    clearToast()

    const validationErrors: Array<'email' | 'password' | 'confirmPassword'> = []
    if (!email || !(await validateEmailWithDomain(email))) {
      validationErrors.push('email')
    }
    if (!password || !validatePassword(password)) {
      setPasswordError(true)
      validationErrors.push('password')
    }
    const confirmMessage = confirmPasswordMessage(password, confirmPassword)
    if (confirmMessage) {
      setConfirmPasswordError(confirmMessage)
      validationErrors.push('confirmPassword')
    }
    if (validationErrors.length > 0) {
      showToast(AuthMessages.VALIDATION_SUMMARY, 'error')
      return
    }

    setIsLoading(true)

    try {
      console.log('[AuthenticationDialog] Starting sign up process')
      // The email is the account's login key, as it already is for Google and invited
      // accounts; people are shown by their name, never the username (SC-438).
      const authResponse = await authService.signUp(
        email.trim().toLowerCase(),
        email,
        password,
        {
          avatar: 'adam',
          gameCharacter: 'adam',
        },
        // The version from the lookup that rendered the notice above the button. [SC-360]
        buildSignupConsent(marketingEmailOptIn, legalLinks.termsVersion)
      )

      // Check if email is verified - new users will have emailVerified = false
      // For OAuth users, emailVerified might be true or undefined
      const emailVerified = authResponse.user.emailVerified !== undefined
        ? authResponse.user.emailVerified
        : false

      console.log('[AuthenticationDialog] Sign up response received:', {
        userId: authResponse.user.id,
        email: authResponse.user.email,
        emailVerified: authResponse.user.emailVerified,
        emailVerifiedResolved: emailVerified
      })

      const profile = coerceUserProfile(authResponse.profile)
      const userData = userProfileToFullUserData(
        {
          id: authResponse.user.id,
          email: authResponse.user.email,
          username: authResponse.user.username,
          role: (profile.role as UserRole) || 'user',
          createdAt: authResponse.user.createdAt.toISOString(),
          emailVerified,
        },
        profile,
        readParseGoogleLinked()
      )

      dispatch(setUserData(userData))

      // Update player name mapping for immediate UI updates (only if in a room session)
      const displayName = getDisplayName(userData)
      const signUpSessionId = store.getState().user.sessionId
      if (signUpSessionId) {
        dispatch(setPlayerName({ id: signUpSessionId, name: displayName }))
      }

      // Update player name service for consistent name resolution
      // Player name service removed - using centralized nameplate system instead

      // If email is not verified, redirect to verification page
      if (!emailVerified) {
        console.log('[AuthenticationDialog] Email not verified, redirecting to verification page')

        // No send from here. This used to POST /email-verification/send in dev,
        // on the reasoning that the cloud function cannot reach a developer's
        // localhost — true, but /verify-email auto-sends on mount in every
        // environment, so this was a second sender racing it. Two senders and one
        // `emailVerificationCode` slot is what made signup mail two codes with only
        // the later one working (SC-385). The redirect below is the send.
        showToast(AuthMessages.SIGNUP_SUCCESS_VERIFICATION, 'success')
        // Short delay so the toast can mount; avoid long dashboard/profile flash before verify
        window.setTimeout(() => {
          window.location.replace('/verify-email')
        }, 350)
        return
      }

      dispatch(setAuthenticated(true))
      console.log('[AuthenticationDialog] Sign up successful')

    } catch (err: any) {
      console.error('[AuthenticationDialog] Sign up error:', err)
      const authError = err as AuthError

      // Like sign-in, the message and its placement live in signUpErrorView so the branch
      // order can be tested without rendering the dialog. [SC-409]
      const view = signUpErrorView(authError)
      if (view.inline === 'email') {
        setEmailError(true)
        setEmailErrorMessage(view.message)
      } else if (view.inline === 'password') {
        setPasswordError(true)
      }
      if (view.highlightGoogle) setHighlightGoogleButton(true)
      if (view.toast) showToast(view.message, 'error')
      setError({
        ...authError,
        message: view.message,
        ...(view.errorField ? { field: view.errorField } : {}),
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Google OAuth login implementation
  const handleGoogleLogin = async () => {
    setHighlightGoogleButton(false)
    clearAllErrors()
    clearToast()
    setIsLoading(true)
    setGoogleLoading(true)

    // Clear any stored OAuth errors when starting a new flow
    sessionStorage.removeItem('oauth_domain_error')
    sessionStorage.removeItem('oauth_error')

    try {
      console.log('[AuthenticationDialog] Starting Google OAuth flow')

      // Get the redirect URI for this application
      // Use the actual current origin to match the running application
      const redirectUri = `${window.location.origin}/auth/google/callback`

      console.log('[AuthenticationDialog] Redirect URI:', redirectUri)
      console.log('[AuthenticationDialog] Window location origin:', window.location.origin)
      console.log('[AuthenticationDialog] Window location hostname:', window.location.hostname)
      console.log('[AuthenticationDialog] Window location port:', window.location.port)

      // Get Google OAuth URL from cloud function (includes state for CSRF protection)
      const { url, state } = await authService.getGoogleOAuthURL(redirectUri)

      console.log('[AuthenticationDialog] Google OAuth URL generated with state:', state ? state.substring(0, 10) + '...' : 'none')

      // Store the state and redirect URI for validation on callback
      if (state) {
        sessionStorage.setItem('google_oauth_state', state)
      }
      sessionStorage.setItem('google_oauth_redirect_uri', redirectUri)
      writeGoogleOAuthHandoffToLocalStorage(state, redirectUri)
      // The redirect drops all React state, so "Sign up with Google" carries the checkbox and
      // the Terms version its notice was shown with across it in storage. Any other start
      // removes a leftover value, so an abandoned sign-up can never apply its choice to a
      // later sign-in. The value is bound to this flow's state, so another tab's callback
      // can never read it from the shared localStorage. [SC-360]
      writeGoogleOAuthSignupConsent(
        tabValue === 1 ? buildSignupConsent(marketingEmailOptIn, legalLinks.termsVersion) : null,
        state
      )

      // Clear any previous OAuth errors
      sessionStorage.removeItem('oauth_error')
      sessionStorage.removeItem('oauth_domain_error')

      console.log('[AuthenticationDialog] Redirecting to Google OAuth...')

      // Redirect to Google OAuth
      window.location.href = url

    } catch (err: any) {
      console.error('Google auth error:', err)
      const isNetwork =
        err.message?.includes('network') ||
        err.message?.includes('fetch') ||
        err.message?.includes('connection')
      const msg =
        err.field === 'domain'
          ? err.message || AuthMessages.OAUTH_DOMAIN_BLOCKED
          : isNetwork
            ? AuthMessages.OAUTH_GET_URL_NETWORK
            : err.message || AuthMessages.OAUTH_GET_URL_DEFAULT

      if (err.field === 'domain') {
        setEmailError(true)
        setEmailErrorMessage(msg)
        setError({ code: 999, message: msg, field: 'domain' })
      } else {
        showToast(msg, 'error')
        setError({ code: 999, message: msg, field: err.field || 'google' })
      }
      setIsLoading(false)
      setGoogleLoading(false)
    }
  }

  // Google button content
  const googleButtonContent = (
    <button
      type="button"
      className={`sevv-auth-google-button${highlightGoogleButton ? ' sevv-auth-google-button--highlight' : ''}`}
      onClick={handleGoogleLogin}
      disabled={googleLoading || isLoading}
    >
      {googleLoading ? (
        <CircularProgress size={24} color="inherit" />
      ) : (
        <>
          <GoogleIcon className="sevv-auth-google-icon" />
          {tabValue === 0 ? 'Continue with Google' : 'Sign up with Google'}
        </>
      )}
    </button>
  )

  // Auto-close forgot password modal after success
  useEffect(() => {
    if (forgotPasswordSuccess && showForgotPasswordModal) {
      const timer = setTimeout(() => {
        setShowForgotPasswordModal(false)
        setForgotPasswordEmail('')
        setForgotPasswordError(null)
        setForgotPasswordSuccess(false)
      }, 3000) // Auto-close after 3 seconds

      return () => clearTimeout(timer)
    }
  }, [forgotPasswordSuccess, showForgotPasswordModal])

  // Forgot Password handlers
  const handleForgotPasswordClick = () => {
    setShowForgotPasswordModal(true)
    setForgotPasswordEmail(email) // Pre-fill with current email if available
    setForgotPasswordError(null)
    setForgotPasswordSuccess(false)
  }

  const handleForgotPasswordClose = () => {
    setShowForgotPasswordModal(false)
    setForgotPasswordEmail('')
    setForgotPasswordError(null)
    setForgotPasswordSuccess(false)
  }

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotPasswordError(null)
    setForgotPasswordSuccess(false)

    if (!forgotPasswordEmail || !validateEmail(forgotPasswordEmail)) {
      setForgotPasswordError(AuthMessages.FORGOT_PASSWORD_EMAIL_INVALID)
      return
    }

    setForgotPasswordLoading(true)

    try {
      // Call password reset server endpoint (no cloud function)
      const baseUrl = getServerBaseUrl()
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const fullUrl = isDev ? '/api/auth/password-reset/request' : `${baseUrl}/api/auth/password-reset/request`

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: isDev ? 'same-origin' : 'include',
        body: JSON.stringify({
          email: forgotPasswordEmail.trim().toLowerCase()
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to request password reset')
      }

      setForgotPasswordSuccess(true)
    } catch (err: any) {
      console.error('Password reset error:', err)
      setForgotPasswordError(AuthMessages.FORGOT_PASSWORD_SUCCESS)
      setForgotPasswordSuccess(true)
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  // SC-438: the panel sells the product for the tab on screen.
  const brandCopy = AUTH_BRAND_COPY[tabValue === 0 ? 'signIn' : 'signUp']

  return (
    <div className="sevv-auth-dialog">
      <div className="sevv-auth-backdrop">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.19, 1.0, 0.22, 1.0] }}
        >
          <div className="sevv-auth-container">
            <div className="sevv-auth-form-section">
              <div className="sevv-auth-logo-container">
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="sevv-auth-logo"
                >
                  <img
                    src={logoUrlState}
                    alt={`${platformName} Logo`}
                    onLoad={() => setLogoLoaded(true)}
                  />
                </motion.div>
                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <h1 className="sevv-auth-platform-name">
                    {platformName}
                  </h1>
                </motion.div>
              </div>

              <div className="sevv-auth-tabs-container">
                <div className="sevv-auth-tabs">
                  <button
                    className={`sevv-auth-tab ${tabValue === 0 ? 'sevv-auth-tab-active' : ''}`}
                    onClick={() => handleTabChange({} as React.SyntheticEvent, 0)}
                    type="button"
                  >
                    Sign In
                  </button>
                  {isRegistrationClosed ? (
                    // Closed sign-up is the go-to-market waitlist: Create Account opens the
                    // marketing waitlist in a new tab instead of the sign-up form, so this
                    // dialog stays where it was. [SC-443]
                    <Tooltip title={AuthMessages.SIGNUP_CLOSED_NOTICE} describeChild arrow>
                      <a
                        className="sevv-auth-tab"
                        href={waitlistUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Create Account ${AuthMessages.OPENS_IN_NEW_TAB}`}
                      >
                        Create Account
                        <OpenInNewIcon fontSize="inherit" aria-hidden />
                      </a>
                    </Tooltip>
                  ) : (
                    <button
                      className={`sevv-auth-tab ${tabValue === 1 ? 'sevv-auth-tab-active' : ''}`}
                      onClick={() => handleTabChange({} as React.SyntheticEvent, 1)}
                      type="button"
                    >
                      Create Account
                    </button>
                  )}
                </div>
              </div>

              {pendingInvite && (
                <Typography
                  variant="body2"
                  data-testid="auth-invite-notice"
                  sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}
                >
                  {tabValue === 1 ? 'Create your account' : 'Sign in'} to join{' '}
                  <b>{pendingInvite.companyName || 'your team'}</b>.
                </Typography>
              )}

              {/* Error messages are now shown inline with fields */}

              <TabPanel value={tabValue} index={0}>
                <form className="sevv-auth-form" onSubmit={handleSignIn} noValidate>
                  <TextInput
                    id="auth-signin-email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Business Email"
                    autoComplete="email"
                    required
                    hasError={emailError}
                    errorId="auth-signin-email-error"
                    errorMessage={emailErrorMessage}
                    helpText={tabValue === 1 && allowedDomainsText ? allowedDomainsText : undefined}
                  />

                  <PasswordInput
                    variant="auth"
                    id="auth-signin-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setPasswordError(false)
                    }}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    placeholder="Password"
                    autoComplete="current-password"
                    hasError={!!signInPasswordError}
                    errorId="auth-signin-password-error"
                    errorMessage={signInPasswordError}
                  />

                  <div className="sevv-auth-forgot-password">
                    <Link
                      component="button"
                      type="button"
                      onClick={handleForgotPasswordClick}
                      className="sevv-auth-forgot-password-link"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="sevv-auth-action-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In to Dashboard'
                    )}
                  </button>

                  <div className="sevv-auth-divider">
                    <span className="sevv-auth-divider-text">OR</span>
                  </div>

                  {googleButtonContent}

                  {/* Kept on Sign In because Google can create an account from here. [SC-360] */}
                  <TermsNotice
                    className="sevv-auth-footer-text"
                    action={TERMS_NOTICE_ACTION.signIn}
                    links={legalLinks}
                  />
                </form>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <form
                  className="sevv-auth-form"
                  onSubmit={handleSignUp}
                  onPointerDown={signUpPasswordBlur.onFormPointerDown}
                  noValidate
                >
                  <TextInput
                    id="auth-signup-email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Business Email"
                    autoComplete="email"
                    required
                    hasError={!!(emailError || (error && error.field === 'email'))}
                    errorId="auth-signup-email-error"
                    errorMessage={emailErrorMessage || (error && error.field === 'email' ? error?.message : '')}
                    helpText={tabValue === 1 && allowedDomainsText ? allowedDomainsText : undefined}
                  />

                  <PasswordInput
                    variant="auth"
                    id="auth-signup-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setPasswordError(false)
                      validatePassword(e.target.value)
                    }}
                    onFocus={() => {
                      signUpPasswordBlur.onFieldFocus()
                      setPasswordFocused(true)
                    }}
                    onBlur={signUpPasswordBlur.onFieldBlur}
                    placeholder="Create Password"
                    autoComplete="new-password"
                    hasError={!!signUpPasswordError}
                    errorId="auth-signup-password-error"
                    errorMessage={signUpPasswordError}
                  />

                  {/* Fade alone keeps the hidden hint's height in the layout; Collapse lets it
                      take no space while hidden and push the fields below down when shown. */}
                  <Collapse
                    in={showPasswordRequirements}
                    className="sevv-auth-password-requirements-collapse"
                  >
                    <Fade in={showPasswordRequirements}>
                      <div className="sevv-auth-password-requirements">
                        <div>Password must:</div>
                        {securitySettings ? (
                          <>
                            <div className={`sevv-auth-password-requirement ${password.length >= securitySettings.password.minLength ? 'sevv-auth-password-requirement-valid' : ''}`}>
                              Be at least {securitySettings.password.minLength} characters long
                            </div>
                            {securitySettings.password.requireUppercase && (
                              <div className={`sevv-auth-password-requirement ${/[A-Z]/.test(password) ? 'sevv-auth-password-requirement-valid' : ''}`}>
                                Contain at least one uppercase letter
                              </div>
                            )}
                            {securitySettings.password.requireLowercase && (
                              <div className={`sevv-auth-password-requirement ${/[a-z]/.test(password) ? 'sevv-auth-password-requirement-valid' : ''}`}>
                                Contain at least one lowercase letter
                              </div>
                            )}
                            {securitySettings.password.requireNumbers && (
                              <div className={`sevv-auth-password-requirement ${/\d/.test(password) ? 'sevv-auth-password-requirement-valid' : ''}`}>
                                Contain at least one number
                              </div>
                            )}
                            {securitySettings.password.requireSpecialChars && (
                              <div className={`sevv-auth-password-requirement ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'sevv-auth-password-requirement-valid' : ''}`}>
                                Contain at least one special character
                              </div>
                            )}
                          </>
                        ) : (
                          <div className={`sevv-auth-password-requirement ${password.length >= 8 ? 'sevv-auth-password-requirement-valid' : ''}`}>
                            Be at least 8 characters long
                          </div>
                        )}
                      </div>
                    </Fade>
                  </Collapse>

                  <PasswordInput
                    variant="auth"
                    id="auth-signup-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      setConfirmPasswordError('')
                    }}
                    // Checked on leaving the field, not on every key, so a half-typed password
                    // isn't called wrong.
                    onBlur={() => {
                      if (confirmPassword) setConfirmPasswordError(confirmPasswordMessage(password, confirmPassword))
                    }}
                    placeholder="Confirm Password"
                    autoComplete="new-password"
                    hasError={!!confirmPasswordError}
                    errorId="auth-signup-confirm-password-error"
                    errorMessage={confirmPasswordError}
                  />

                  {/* The card is a fixed white while the token colours turn light in dark
                      mode, so the class lets the dialog's scoped SCSS out-specify them. */}
                  <MarketingEmailCheckbox
                    id="auth-signup-marketing-email"
                    checked={marketingEmailOptIn}
                    onChange={setMarketingEmailOptIn}
                    className="sevv-auth-marketing-checkbox"
                    sx={{ mt: 1 }}
                  />

                  {/* Directly above the button it applies to, as in the approved prototype. */}
                  <TermsNotice
                    className="sevv-auth-footer-text sevv-auth-terms-above-action"
                    action={TERMS_NOTICE_ACTION.createAccount}
                    links={legalLinks}
                  />

                  <button
                    type="submit"
                    className="sevv-auth-action-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Create Business Account'
                    )}
                  </button>

                  <div className="sevv-auth-divider">
                    <span className="sevv-auth-divider-text">OR</span>
                  </div>

                  {googleButtonContent}
                </form>
              </TabPanel>
            </div>

            {!isMobile && (
              <div className="sevv-auth-brand-section">
                <div className="sevv-auth-brand-content">
                  <h2 className="sevv-auth-brand-title">{brandCopy.title}</h2>

                  <p className="sevv-auth-brand-subtitle">{brandCopy.subtitle}</p>

                  <ul className="sevv-auth-feature-list">
                    {brandCopy.points.map(({ icon: Icon, text }) => (
                      <li className="sevv-auth-feature-item" key={text}>
                        <Icon className="sevv-auth-feature-icon" />
                        <span className="sevv-auth-feature-text">{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {toast && (
        <div
          className={`sevv-auth-toast ${toast.severity === 'success' ? 'sevv-auth-toast-success' : ''}`}
          role="alert"
          aria-live="assertive"
        >
          <div className="sevv-auth-toast-content">
            {toast.message}
            <IconButton
              size="small"
              onClick={handleToastClose}
              aria-label="Dismiss message"
              sx={{
                ml: 1,
                color: 'inherit',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      {/* Forgot Password Modal — shared Sevv dialog shell (see forgotPasswordDialogSx) */}
      <Dialog
        open={showForgotPasswordModal}
        onClose={handleForgotPasswordClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: forgotPasswordDialogPaperSx }}
      >
        <DialogTitle sx={forgotPasswordDialogTitleSx}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: 'inherit' }}>
            Reset Password
          </Typography>
          <IconButton
            onClick={handleForgotPasswordClose}
            size="small"
            aria-label="Close"
            sx={{ color: 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {forgotPasswordSuccess ? (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="body1" color="success.main" sx={{ mb: 2, fontWeight: 600 }}>
                Password reset email sent!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {AuthMessages.FORGOT_PASSWORD_SUCCESS}
              </Typography>
            </Box>
          ) : (
            <form id="auth-forgot-password-form" onSubmit={handleForgotPasswordSubmit}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address and we&apos;ll send you a link to reset your password.
              </Typography>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                error={!!forgotPasswordError}
                helperText={forgotPasswordError}
                autoFocus
                required
                margin="normal"
                sx={{
                  mt: 0,
                  mb: 1,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </form>
          )}
        </DialogContent>
        <DialogActions sx={forgotPasswordDialogActionsSx}>
          {forgotPasswordSuccess ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleForgotPasswordClose}
              sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44, px: 3 }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleForgotPasswordClose}
                disabled={forgotPasswordLoading}
                sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44, px: 2 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="auth-forgot-password-form"
                variant="contained"
                color="primary"
                disabled={forgotPasswordLoading || !forgotPasswordEmail}
                sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44, px: 3 }}
              >
                {forgotPasswordLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Google sign-in refused a new account while sign-up is closed [SC-443] */}
      <Dialog
        open={showSignupClosedDialog}
        onClose={() => setShowSignupClosedDialog(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="auth-signup-closed-title"
        aria-describedby="auth-signup-closed-body"
        PaperProps={{ sx: forgotPasswordDialogPaperSx }}
      >
        <DialogTitle id="auth-signup-closed-title" sx={forgotPasswordDialogTitleSx}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: 'inherit' }}>
            {AuthMessages.OAUTH_SIGNUP_CLOSED_TITLE}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            id="auth-signup-closed-body"
            variant="body2"
            color="text.secondary"
            sx={{ pt: 2 }}
          >
            {AuthMessages.OAUTH_SIGNUP_CLOSED_BODY}
          </Typography>
        </DialogContent>
        <DialogActions sx={forgotPasswordDialogActionsSx}>
          <Button
            onClick={() => setShowSignupClosedDialog(false)}
            autoFocus
            sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44, px: 3 }}
          >
            OK
          </Button>
          <Button
            variant="contained"
            color="primary"
            component="a"
            href={waitlistUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${AuthMessages.SIGNUP_WAITLIST_CTA} ${AuthMessages.OPENS_IN_NEW_TAB}`}
            endIcon={<OpenInNewIcon aria-hidden />}
            onClick={() => setShowSignupClosedDialog(false)}
            sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44, px: 3 }}
          >
            {AuthMessages.SIGNUP_WAITLIST_CTA}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
